import { branchFeatureFlags } from "./branchFeatureFlags";

/**
 * Level 3 signing: how the Level 3 officer signs the Demand Note and the RL
 * Certificate before forwarding. "Digital" means the Apply DSC button, which
 * signs the generated PDF with the officer's USB token. "Manual" means the
 * older way: download the PDF, sign it outside the software and upload it
 * back.
 */

export const L3_SIGNING_MODES = {
  /** Only Apply DSC is offered; the Browse / drag-and-drop upload is hidden. */
  DIGITAL_ONLY: "DIGITAL_ONLY",
  /** Apply DSC and the manual upload are both offered. */
  BOTH: "BOTH",
  /** Only the manual upload is offered; Apply DSC is not rendered. */
  MANUAL_ONLY: "MANUAL_ONLY",
} as const;

export type L3SigningMode =
  (typeof L3_SIGNING_MODES)[keyof typeof L3_SIGNING_MODES];

/**
 * The mode set in branchFeatureFlags. An unknown or missing value falls back
 * to MANUAL_ONLY, so a typing mistake in the flag can never hide the upload
 * that officers rely on today.
 */
export function getL3SigningMode(): L3SigningMode {
  const mode = branchFeatureFlags.L3_SIGNING_MODE as L3SigningMode;

  return Object.values(L3_SIGNING_MODES).includes(mode)
    ? mode
    : L3_SIGNING_MODES.MANUAL_ONLY;
}

/** True when the Apply DSC button is shown to the Level 3 officer. */
export function isDigitalSigningEnabled(): boolean {
  return getL3SigningMode() !== L3_SIGNING_MODES.MANUAL_ONLY;
}

/** True when the manual Browse / drag-and-drop upload is shown. */
export function isManualUploadEnabled(): boolean {
  return getL3SigningMode() !== L3_SIGNING_MODES.DIGITAL_ONLY;
}

/**
 * A browser cannot read a USB token, so the signing itself is done by a small
 * signer program installed on the officer's own computer. Everything this
 * application needs to know about that program is set here, so pointing it at
 * a different signer program is a change to this block only:
 *   baseUrl        - the address the program listens on.
 *   checkTokenPath - call that confirms the USB token is plugged in. Leave it
 *                    as "" when the program has no such call; the check is
 *                    then skipped and the officer goes straight to the PIN.
 *   signPath       - call that signs the PDF.
 *   fileField      - name of the form field that carries the PDF.
 *   metadataField  - name of the form field that carries the token, PIN and
 *                    stamp details as JSON.
 *   signedPdfField - name of the response field holding the signed PDF as
 *                    base64 text.
 *   timeoutMs      - how long to wait for the program before giving up.
 */
export const DSC_SIGNER = {
  baseUrl: "http://127.0.0.1:5000",
  checkTokenPath: "/check-hsm",
  signPath: "/sign",
  fileField: "file",
  metadataField: "metadata",
  signedPdfField: "signed_pdf_base64",
  timeoutMs: 60000,
} as const;

export type DscToken = {
  /** Name shown to the officer in the token dropdown. */
  name: string;
  /** Identifier the signer program knows the token by. */
  value: string;
  /** Path of the token's PKCS#11 driver on the officer's Windows computer. */
  driverPath: string;
};

/**
 * USB tokens the officer can pick from. The driver path is where the token's
 * own driver installer places its PKCS#11 file; add a row here for any other
 * token brand.
 */
export const DSC_TOKENS: readonly DscToken[] = [
  {
    name: "ePass 2003 Auto",
    value: "ePass2003",
    driverPath: "C:\\Windows\\System32\\eps2003csp11v2.dll",
  },
  {
    name: "mToken",
    value: "mToken CryptoID",
    driverPath: "C:\\Windows\\System32\\CryptoIDA_pkcs11.dll",
  },
  {
    name: "Watch Data (WD ProxKey)",
    value: "WD PROXKey",
    driverPath: "C:\\Windows\\System32\\SignatureP11.dll",
  },
];

export const SIGNABLE_DOCUMENTS = {
  DEMAND_NOTE: "demand-note",
  CERTIFICATE: "certificate",
} as const;

export type SignableDocument =
  (typeof SIGNABLE_DOCUMENTS)[keyof typeof SIGNABLE_DOCUMENTS];

export type DscStampPlacement = {
  /**
   * Text printed in the template's signature block. The stamp is placed just
   * above the last line that contains it, on whichever page that is, so it
   * follows the signature block as the document grows or shrinks.
   */
  anchorText: string;
  /** Space, in PDF points, left between the anchor text and the stamp. */
  anchorGap: number;
  /** Page used when the anchor text is not found in the PDF. */
  page: "last" | "first";
  /**
   * "x, y, width, height" in PDF points, from the bottom-left corner. Width
   * and height are always the size of the stamp; x and y are used only when
   * the anchor text is not found.
   */
  signaturebox: string;
  /** Designation printed in the stamp under the signer's name. */
  designation: string;
};

/**
 * Where the visible stamp sits on each document: in the blank space above
 * "Chief Officer" in the template's signature block. The Certificate prints
 * the officer's name on the line above "Chief Officer", so its gap is one
 * line taller to clear that name.
 */
/*
 * BEFORE - the stamp was a four-line box of 160 x 48 points:
 *   signaturebox: "380, 90, 160, 48",   (both documents)
 * The stamp now carries a "Signature valid" heading, a tick and six lines, so
 * it is 200 x 80 points.
 *
 * BEFORE - 200 x 80 points:
 *   signaturebox: "380, 90, 200, 80",   (both documents)
 * The stamp is now 200 x 60. The signer draws the "Signature valid" heading
 * at a fifth of the stamp's height, so it shrinks from 16 to 12 points, and
 * a shorter stamp needs less room above "Chief Officer". The officers'
 * config.json now prints four lines instead of six, which still fit.
 *
 * BEFORE - 200 x 60 points:
 *   signaturebox: "380, 90, 200, 60",   (both documents)
 * The stamp is now 200 x 48. The signer keeps the top 30% of the stamp for
 * the heading and puts the lines at the bottom, so with four lines a 60 pt
 * stamp left an empty band between "Signature valid" and the lines. At 48
 * the four lines fill the rest, the heading is 9.6 pt, and on the Bahadura
 * Demand Note the heading no longer touches the TOTAL row. Below 48 the
 * lines start to shrink.
 */
export const DSC_STAMP_PLACEMENT: Record<SignableDocument, DscStampPlacement> = {
  [SIGNABLE_DOCUMENTS.DEMAND_NOTE]: {
    anchorText: "Chief Officer",
    anchorGap: 4,
    page: "last",
    signaturebox: "380, 90, 200, 48",
    designation: "Chief Officer",
  },
  [SIGNABLE_DOCUMENTS.CERTIFICATE]: {
    anchorText: "Chief Officer",
    anchorGap: 20,
    page: "last",
    signaturebox: "380, 90, 200, 48",
    designation: "Chief Officer",
  },
};

/**
 * Lines of the stamp that are the same for every document and every
 * application. The signer program prints each one after its own label
 * ("Reason:", "Location:", "Project Code :"). The Project Code is this
 * municipality's own code and does not change from one signature to the next.
 */
export const DSC_STAMP_DETAILS = {
  reason: "Approved",
  location: "Bahadura Nagarpanchayat",
  projectCode: "BDNP-26-91556",
} as const;

export const DSC_MESSAGES = {
  NO_DOCUMENT:
    "The document to sign was not found. Go Back, press Save And Preview again, then press Next.",
  SIGNER_NOT_REACHABLE:
    "The signer program on this computer did not respond. Check that it is running and the USB token is plugged in.",
  SIGNED: "Document signed. Check the preview, then press Upload and Forward.",
  LOG_NOT_SAVED:
    "The document is signed, but the signing could not be written to the Audit History.",
} as const;
